// navScanner.js — compares DashboardLayout navItems against the relation map registry
// When a new nav item is added to DashboardLayout.js, this detects it as "unregistered"
// and prompts the developer to add it to relationMap.config.js

import { navItems } from "../../layout/DashboardLayout";

// Nav items that are UI views/shells, not platform components.
// These are intentionally excluded from the map.
const UI_VIEWS = new Set([
  "Dashboard", "Vendor Dashboard", "3PL Dashboard", "Customs Dashboard",
  "My Dashboard", "ATT Review Portal", "Platform Overview",
  "Create Shipment", "Shipment Tracking", "Chat with Partners", "Messages",
  "Manage Requests", "My Support Tickets", "Support Tickets",
  "My Shipments", "Notifications", "General History", "Operational Data",
  "Inbound Shipments (ASN)", "Landing Evaluations", "HS Code Evaluations",
  "DG Shipments", "ATT Accreditation", "Stock Reconciliation", "Order Admin",
  "Payable Accounts", "API Partners", "Workflow", "Master Data",
  "All Shipments", "User Management", "Tenant Management", "Subscriptions",
  "Companies", "Organizations", "Customer Tickets", "Customer Accounts", "Partners & Vendors (API)", "My Tickets", "Platform Announcements", "HS Code Management", "Customs Entries",
  "Revenue Analytics", "Global Audit", "System Health", "Traffic Analytics",
  "Excel Converter", "Email Center", "Financial Reports", "SLA Contracts",
  "EDI Management", "Warehouse Management (WMS)",
]);

const STOP_WORDS = new Set(["and", "the", "for", "with", "from", "my", "all", "3pl", "att", "asn"]);

function keywords(label) {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9]/g, " ")
        .split(/\s+/)
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));
}

function bestMatch(navLabel, mapNodes) {
    const navWords = new Set(keywords(navLabel));
    let best = { node: null, score: 0 };
    for (const node of mapNodes) {
        const nodeWords = new Set(keywords(node.label));
        const shared = [...navWords].filter(w => nodeWords.has(w)).length;
        if (shared > best.score) best = { node, score: shared };
    }
    return best;
}

// Returns all unique nav items (deduplicated by label) with match status.
// Each item: { label, path, roles[], isUIView, isMatched, matchedNode }
export function scanNav(mapNodes) {
    const seen = new Map(); // label → item

    Object.entries(navItems).forEach(([role, items]) => {
        items.forEach(item => {
            if (!item.path) return; // section headers
            if (seen.has(item.label)) {
                seen.get(item.label).roles.push(role);
            } else {
                seen.set(item.label, { label: item.label, path: item.path, roles: [role] });
            }
        });
    });

    return [...seen.values()].map(item => {
        const isUIView = UI_VIEWS.has(item.label);
        const match    = isUIView ? { node: null, score: 0 } : bestMatch(item.label, mapNodes);
        return {
            ...item,
            isUIView,
            isMatched: match.score >= 1,
            matchedNode: match.node,
        };
    }).sort((a, b) => {
        // Unmatched platform items first, then matched, then UI views
        if (!a.isUIView && !a.isMatched && (b.isUIView || b.isMatched)) return -1;
        if (!b.isUIView && !b.isMatched && (a.isUIView || a.isMatched)) return 1;
        return a.label.localeCompare(b.label);
    });
}

// Count items that need attention (not UI views, not matched)
export function countUnregistered(mapNodes) {
    return scanNav(mapNodes).filter(i => !i.isUIView && !i.isMatched).length;
}

// Role color mapping for badges
export const ROLE_COLORS = {
    client:       { bg: "#f97316", text: "#fff7ed" },
    operator:     { bg: "#3b82f6", text: "#eff6ff" },
    admin:        { bg: "#8b5cf6", text: "#f5f3ff" },
    agent:        { bg: "#10b981", text: "#f0fdf4" },
    "3pl":        { bg: "#0ea5e9", text: "#f0f9ff" },
    vendor:       { bg: "#eab308", text: "#fefce8" },
    customer:     { bg: "#94a3b8", text: "#f8fafc" },
    "super-admin": { bg: "#ef4444", text: "#fef2f2" },
};
